
import React from "react";
import { Project } from "@/types/project";
import { ProjectHeader } from "@/components/project/ProjectHeader";
import { RequirementsTable } from "@/components/project/RequirementsTable";
import { useRequirementStatus } from "@/hooks/useRequirementStatus";
import { RequirementGuidance } from "@/components/project/RequirementGuidance";
import { ProjectStats } from "@/components/project/ProjectStats";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

interface ProjectDetailContentProps {
  project: Project;
}

export function ProjectDetailContent({ project }: ProjectDetailContentProps) {
  const navigate = useNavigate();
  const { 
    requirementStatuses, 
    isStatusLoading, 
    getRequirementStatus, 
    handleStatusChange 
  } = useRequirementStatus(project.id);
  
  const {
    handleViewGuidance,
    GuidanceDrawerComponent
  } = RequirementGuidance({ projectId: project.id });

  const handleUploadDocumentation = (requirement: any) => {
    console.log("Upload documentation for requirement:", requirement);
    
    // Update status to "In Progress" when documentation is being uploaded
    handleStatusChange(requirement, "In Progress");
  };

  const handleMarkNotApplicable = async (requirement: any) => {
    const success = await handleStatusChange(requirement, "Not Applicable");
    if (success) {
      toast.info(`Marked requirement ${requirement.identifier || ""} as not applicable`);
    }
  };

  const handleMarkReadyForReview = async (requirement: any) => {
    const success = await handleStatusChange(requirement, "Ready to Submit");
    if (success) {
      toast.success(`Marked requirement ${requirement.identifier || ""} as ready for review`);
    }
  };

  const handleMarkIncomplete = async (requirement: any) => {
    const success = await handleStatusChange(requirement, "Incomplete");
    if (success) {
      toast.info(`Marked requirement ${requirement.identifier || ""} as incomplete`);
    }
  };

  const handleGoBack = () => {
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <ProjectHeader />
      
      <div className="container py-6">
        <Button 
          variant="ghost" 
          size="sm" 
          className="mb-6" 
          onClick={handleGoBack}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          All Projects
        </Button>

        <div className="space-y-8">
          <div className="bg-card border rounded-lg p-6 shadow-sm">
            <div className="space-y-4">
              <div>
                <h1 className="text-2xl font-bold">{project.title}</h1>
                {project.description && (
                  <p className="text-muted-foreground mt-1">{project.description}</p>
                )}
              </div>
              
              {project.standard && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground border-t pt-3 mt-2">
                  <span className="font-medium">{project.standard.title}</span>
                  
                  {project.standard.category && (
                    <span className="px-2 py-0.5 bg-muted rounded-full text-xs">
                      {project.standard.category}
                    </span>
                  )}
                  
                  {project.standard.identifier && (
                    <span className="flex items-center gap-1 after:content-['•'] after:ml-2 last:after:content-none">
                      <span className="text-muted-foreground">ID:</span> 
                      <span>{project.standard.identifier}</span>
                    </span>
                  )}
                  
                  {project.standard.version && (
                    <span className="flex items-center gap-1 after:content-['•'] after:ml-2 last:after:content-none">
                      <span className="text-muted-foreground">Version:</span> 
                      <span>{project.standard.version}</span>
                    </span>
                  )}
                  
                  {project.standard.status && (
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Status:</span> 
                      <span className="inline-block bg-green-100 text-green-800 rounded-full px-2 py-0.5 text-xs">
                        {project.standard.status}
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>

            <ProjectStats 
              requirements={project.requirements || []} 
              requirementStatuses={requirementStatuses} 
            />
          </div>

          <RequirementsTable 
            requirements={project.requirements || []}
            onViewGuidance={handleViewGuidance}
            onUploadDocumentation={handleUploadDocumentation}
            onMarkNotApplicable={handleMarkNotApplicable}
            onMarkReadyForReview={handleMarkReadyForReview}
            onMarkIncomplete={handleMarkIncomplete}
            requirementStatuses={requirementStatuses}
            isStatusLoading={isStatusLoading}
            getRequirementStatus={getRequirementStatus}
            projectId={project.id}
          />
        </div>
      </div>

      {GuidanceDrawerComponent}
    </div>
  );
}
